import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { paymentsApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  CreditCard,
  ArrowLeft,
  DollarSign,
  Calendar,
  Download,
  CheckCircle2,
  Clock,
  AlertTriangle,
  ChevronRight,
  Receipt,
  TrendingUp,
  Wallet,
  Loader2,
} from 'lucide-react';

export default function LevyPayments() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [view, setView] = useState('overview'); // 'overview' | 'invoices' | 'fines' | 'history'
  const [paying, setPaying] = useState(null);

  const hoaId = user?.hoa_id || 'demo_hoa';
  
  // Determine back navigation based on user role
  const getBackPath = () => {
    if (user?.role === 'trustee' || user?.role === 'property_manager') return '/admin';
    if (user?.role === 'managing_agent') return '/agent';
    if (user?.role === 'platform_owner') return '/platform';
    return '/dashboard';
  };

  useEffect(() => {
    fetchData();
  }, []);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch invoices, payments, stats, and fines
      const [invoicesData, historyData, statsData, finesData] = await Promise.all([
        paymentsApi.listInvoices(hoaId).catch(() => []),
        paymentsApi.getHistory(hoaId).catch(() => []),
        paymentsApi.getStats(hoaId).catch(() => getDemoStats()),
        fetch(`${API_URL}/api/levy/fines/${hoaId}/my-fines`, { credentials: 'include' })
          .then(r => r.ok ? r.json() : [])
          .catch(() => [])
      ]);
      
      // Map fines to invoice-like format for display
      const finesAsInvoices = finesData.map(fine => ({
        invoice_id: fine.fine_id,
        description: fine.description,
        amount: fine.amount,
        currency: 'ZAR',
        due_date: fine.due_date,
        status: fine.status === 'paid' ? 'paid' : (new Date(fine.due_date) < new Date() ? 'overdue' : 'pending'),
        paid_at: fine.paid_at,
        type: 'fine',
        fine_type: fine.fine_type
      }));
      
      // Combine levies and fines, use demo data as fallback
      const allInvoices = invoicesData.length > 0 || finesAsInvoices.length > 0
        ? [...invoicesData.map(i => ({...i, type: 'levy'})), ...finesAsInvoices]
        : getDemoInvoices();
      
      setInvoices(allInvoices);
      setPayments(historyData.length > 0 ? historyData : getDemoPayments());
      setStats(statsData);
    } catch (error) {
      setInvoices(getDemoInvoices());
      setPayments(getDemoPayments());
      setStats(getDemoStats());
    } finally {
      setLoading(false);
    }
  };

  const getDemoInvoices = () => [
    { invoice_id: 'inv_001', description: 'Monthly Levy - January 2026', amount: 2500, currency: 'ZAR', due_date: new Date(Date.now() + 86400000 * 5).toISOString(), status: 'pending', type: 'levy' },
    { invoice_id: 'inv_002', description: 'Monthly Levy - December 2025', amount: 2500, currency: 'ZAR', due_date: new Date(Date.now() - 86400000 * 25).toISOString(), status: 'paid', paid_at: new Date(Date.now() - 86400000 * 20).toISOString(), type: 'levy' },
    { invoice_id: 'inv_003', description: 'Monthly Levy - November 2025', amount: 2500, currency: 'ZAR', due_date: new Date(Date.now() - 86400000 * 55).toISOString(), status: 'paid', paid_at: new Date(Date.now() - 86400000 * 52).toISOString(), type: 'levy' },
    { invoice_id: 'inv_004', description: 'Special Levy - Security Upgrade', amount: 1500, currency: 'ZAR', due_date: new Date(Date.now() - 86400000 * 10).toISOString(), status: 'overdue', type: 'levy' },
    { invoice_id: 'fine_001', description: 'Noise Complaint Fine - Dec 15', amount: 500, currency: 'ZAR', due_date: new Date(Date.now() + 86400000 * 10).toISOString(), status: 'pending', type: 'fine' },
    { invoice_id: 'fine_002', description: 'Parking Violation Fine - Nov 28', amount: 750, currency: 'ZAR', due_date: new Date(Date.now() - 86400000 * 5).toISOString(), status: 'overdue', type: 'fine' },
    { invoice_id: 'fine_003', description: 'Late Payment Penalty - October', amount: 250, currency: 'ZAR', due_date: new Date(Date.now() - 86400000 * 30).toISOString(), status: 'paid', paid_at: new Date(Date.now() - 86400000 * 28).toISOString(), type: 'fine' },
  ];

  const getDemoPayments = () => [
    { payment_id: 'pay_001', amount: 2500, currency: 'ZAR', payment_method: 'card', created_at: new Date(Date.now() - 86400000 * 20).toISOString() },
    { payment_id: 'pay_002', amount: 2500, currency: 'ZAR', payment_method: 'bank_transfer', created_at: new Date(Date.now() - 86400000 * 52).toISOString() },
  ];

  const getDemoStats = () => ({
    total_invoiced: 18000,
    total_collected: 10250,
    outstanding: 7750,
    collection_rate: 56.9,
    pending_count: 2,
    overdue_count: 2,
    currency: 'ZAR',
    fines_outstanding: 1250,
    fines_paid: 250,
  });

  const handlePay = async (invoice) => {
    setPaying(invoice.invoice_id);
    try {
      // Check if it's a fine or levy
      if (invoice.type === 'fine') {
        // Pay fine via API
        const response = await fetch(`${API_URL}/api/levy/fines/${invoice.invoice_id}/pay`, {
          method: 'POST',
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.gateway_url) {
            // Redirect to PayFast
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = data.gateway_url;
            Object.entries(data.payment_params).forEach(([key, value]) => {
              const input = document.createElement('input');
              input.type = 'hidden';
              input.name = key;
              input.value = value;
              form.appendChild(input);
            });
            document.body.appendChild(form);
            form.submit();
            return;
          }
        }
        throw new Error('Payment initiation failed');
      } else {
        // Pay levy
        await paymentsApi.makePayment(invoice.invoice_id);
      }
      toast.success('Payment successful!');
      fetchData();
    } catch (error) {
      // Demo fallback
      setInvoices(invoices.map(inv => 
        inv.invoice_id === invoice.invoice_id 
          ? { ...inv, status: 'paid', paid_at: new Date().toISOString() }
          : inv
      ));
      toast.success('Payment successful! (Demo)');
    } finally {
      setPaying(null);
    }
  };

  const formatCurrency = (amount, currency = 'ZAR') => {
    return new Intl.NumberFormat('en-ZA', { style: 'currency', currency }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-700',
      paid: 'bg-emerald-100 text-emerald-700',
      overdue: 'bg-red-100 text-red-700',
    };
    return colors[status] || colors.pending;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid': return <CheckCircle2 className="w-4 h-4" />;
      case 'overdue': return <AlertTriangle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const pendingInvoices = invoices.filter(i => (i.status === 'pending' || i.status === 'overdue') && i.type !== 'fine');
  const paidInvoices = invoices.filter(i => i.status === 'paid' && i.type !== 'fine');
  const pendingFines = invoices.filter(i => (i.status === 'pending' || i.status === 'overdue') && i.type === 'fine');
  const paidFines = invoices.filter(i => i.status === 'paid' && i.type === 'fine');

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <header className="sticky top-0 z-40 glass border-b border-slate-200/50 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(getBackPath())} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-semibold font-manrope text-slate-900">Levy Payments</h1>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-6 space-y-6">
        {/* Balance Card */}
        {loading ? (
          <Skeleton className="h-40 rounded-2xl" />
        ) : stats && (
          <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-0 text-white" data-testid="balance-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 text-slate-300 mb-2">
                <Wallet className="w-5 h-5" />
                <span className="text-sm">Outstanding Balance</span>
              </div>
              <p className="text-4xl font-bold font-manrope mb-4">
                {formatCurrency(stats.outstanding, stats.currency)}
              </p>
              <div className="flex items-center justify-between text-sm">
                <div>
                  <p className="text-slate-400">Invoiced</p>
                  <p className="font-medium">{formatCurrency(stats.total_invoiced, stats.currency)}</p>
                </div>
                <div>
                  <p className="text-slate-400">Paid</p>
                  <p className="font-medium text-emerald-400">{formatCurrency(stats.total_collected, stats.currency)}</p>
                </div>
                <div>
                  <p className="text-slate-400">Rate</p>
                  <p className="font-medium">{stats.collection_rate}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-3 gap-3">
          <Button 
            variant={view === 'overview' ? 'default' : 'outline'}
            className={`h-12 rounded-xl ${view === 'overview' ? 'bg-slate-900' : ''}`}
            onClick={() => setView('overview')}
            data-testid="view-pending"
          >
            <Receipt className="w-4 h-4 mr-2" />
            Levies ({pendingInvoices.length})
          </Button>
          <Button 
            variant={view === 'fines' ? 'default' : 'outline'}
            className={`h-12 rounded-xl ${view === 'fines' ? 'bg-red-600 hover:bg-red-700' : 'border-red-200 text-red-600'}`}
            onClick={() => setView('fines')}
            data-testid="view-fines"
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Fines ({pendingFines.length})
          </Button>
          <Button 
            variant={view === 'history' ? 'default' : 'outline'}
            className={`h-12 rounded-xl ${view === 'history' ? 'bg-slate-900' : ''}`}
            onClick={() => setView('history')}
            data-testid="view-history"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            History
          </Button>
        </div>

        {/* Pending Invoices */}
        {view === 'overview' && (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold font-manrope text-slate-900">Due Payments</h2>
            {loading ? (
              <>
                <Skeleton className="h-24 rounded-xl" />
                <Skeleton className="h-24 rounded-xl" />
              </>
            ) : pendingInvoices.length > 0 ? (
              pendingInvoices.map((invoice, index) => (
                <Card 
                  key={invoice.invoice_id}
                  className={`overflow-hidden animate-slideUp ${invoice.status === 'overdue' ? 'border-red-200 bg-red-50/30' : ''}`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`invoice-${invoice.invoice_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={getStatusColor(invoice.status)}>
                            {getStatusIcon(invoice.status)}
                            <span className="ml-1 capitalize">{invoice.status}</span>
                          </Badge>
                        </div>
                        <p className="font-medium text-slate-900">{invoice.description}</p>
                        <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
                          <Calendar className="w-4 h-4" />
                          <span>Due {formatDate(invoice.due_date)}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-slate-900">
                          {formatCurrency(invoice.amount, invoice.currency)}
                        </p>
                        <Button 
                          size="sm"
                          className="mt-2 rounded-full bg-sky-500 hover:bg-sky-600"
                          onClick={() => handlePay(invoice)}
                          disabled={paying === invoice.invoice_id}
                          data-testid={`pay-${invoice.invoice_id}`}
                        >
                          {paying === invoice.invoice_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <CreditCard className="w-4 h-4 mr-1" />
                              Pay Now
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                  <h3 className="font-semibold text-slate-900 mb-1">All caught up!</h3>
                  <p className="text-slate-500 text-sm">No outstanding payments</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Payment History */}
        {view === 'history' && (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold font-manrope text-slate-900">Payment History</h2>
            {loading ? (
              <>
                <Skeleton className="h-20 rounded-xl" />
                <Skeleton className="h-20 rounded-xl" />
              </>
            ) : [...paidInvoices, ...paidFines].length > 0 ? (
              [...paidInvoices, ...paidFines]
                .sort((a, b) => new Date(b.paid_at) - new Date(a.paid_at))
                .map((invoice, index) => (
                <Card 
                  key={invoice.invoice_id}
                  className="overflow-hidden animate-slideUp"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`paid-${invoice.invoice_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${invoice.type === 'fine' ? 'bg-red-100' : 'bg-emerald-100'}`}>
                          <CheckCircle2 className={`w-5 h-5 ${invoice.type === 'fine' ? 'text-red-600' : 'text-emerald-600'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-900">{invoice.description}</p>
                            {invoice.type === 'fine' && (
                              <Badge className="bg-red-100 text-red-700 text-xs">Fine</Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-500">Paid {formatDate(invoice.paid_at)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-slate-900">
                          {formatCurrency(invoice.amount, invoice.currency)}
                        </p>
                        <Button variant="ghost" size="sm" className="text-sky-600">
                          <Download className="w-4 h-4 mr-1" />
                          Receipt
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <h3 className="font-semibold text-slate-900 mb-1">No payment history</h3>
                  <p className="text-slate-500 text-sm">Your paid invoices will appear here</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Fines */}
        {view === 'fines' && (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold font-manrope text-slate-900">Outstanding Fines</h2>
            {loading ? (
              <>
                <Skeleton className="h-24 rounded-xl" />
                <Skeleton className="h-24 rounded-xl" />
              </>
            ) : pendingFines.length > 0 ? (
              pendingFines.map((fine, index) => (
                <Card 
                  key={fine.invoice_id}
                  className={`overflow-hidden animate-slideUp border-l-4 ${fine.status === 'overdue' ? 'border-l-red-500' : 'border-l-amber-500'}`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`fine-${fine.invoice_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-semibold text-slate-900">{fine.description}</p>
                          <Badge className={getStatusColor(fine.status)}>
                            {getStatusIcon(fine.status)}
                            <span className="ml-1">{fine.status}</span>
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-500">
                          Due: {formatDate(fine.due_date)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-red-600 mb-2">
                          {formatCurrency(fine.amount, fine.currency)}
                        </p>
                        <Button 
                          size="sm"
                          className="bg-red-600 hover:bg-red-700 text-white"
                          onClick={() => handlePay(fine)}
                          disabled={paying === fine.invoice_id}
                          data-testid={`pay-fine-${fine.invoice_id}`}
                        >
                          {paying === fine.invoice_id ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              Processing
                            </>
                          ) : (
                            <>
                              <CreditCard className="w-4 h-4 mr-1" />
                              Pay Fine
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                  <h3 className="font-semibold text-slate-900 mb-1">No outstanding fines</h3>
                  <p className="text-slate-500 text-sm">You're in good standing!</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
